/**
 * ThreadSectionsManager — every section in one list, the empty ones included
 * (2026-09-09, Miles: "user should be able to change the order at any time —
 * ensure this is the case because it doesn't seem like it"). The rail and the
 * sheet hide an EMPTY section, so it could be neither seen nor moved, and a
 * header move that swapped with a hidden neighbour showed no change at all.
 * Here every section — custom and built-in — is a row with ▲ / ▼, an inline
 * rename, a delete for custom sections, and "new category…" at the foot: the
 * one place the whole order is on screen. A body portal in the rail's dark
 * menu idiom (anchored to the header's ⋯, focus-first, Escape closes — the
 * useAnchoredMenu contract the header owns), reachable from any section
 * header in the rail and the sheet.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CategoryFolderGlyph, ThreadTypeGlyph } from './ThreadTypeGlyph';
import { sectionGlyphKind, type ThreadSection } from './threadSections';
import { threadListCopy } from './threadListCopy';
import type { ThreadOrganizeApi } from './threadOrganize';
import { RailGhostIconButton, RailMenu, RailMenuHeading, RailMenuInput, RailMenuItem } from './organizeStyles';
import { THREAD_CATEGORY_NAME_MAX } from '../../types/threadCategories';

export interface ThreadSectionsManagerProps {
  organize: ThreadOrganizeApi;
  /** EVERY section (buildThreadSections with includeEmpty), in the viewer's order. */
  sections: ReadonlyArray<ThreadSection<unknown>>;
  /** Fixed position, measured from the trigger at open time (the anchored-menu contract). */
  left: number;
  top: number;
  menuRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
}

const ArrowUp: React.FC = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <path d="M2.5 7.5 L6 4 L9.5 7.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ArrowDown: React.FC = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <path d="M2.5 4.5 L6 8 L9.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Pencil: React.FC = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <path d="M2 10 L2.6 7.8 L8.2 2.2 L9.8 3.8 L4.2 9.4 Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);
const Cross: React.FC = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <path d="M3 3 L9 9 M9 3 L3 9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', borderRadius: 6, minWidth: 0,
};
const labelStyle: React.CSSProperties = {
  flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  color: 'var(--brand-gold)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", fontSize: '12.5px', fontWeight: 500,
};
const countStyle: React.CSSProperties = {
  flex: 'none', color: 'rgba(182, 146, 76, 0.7)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", fontSize: '11px',
};

export const ThreadSectionsManager: React.FC<ThreadSectionsManagerProps> = ({
  organize, sections, left, top, menuRef, onClose,
}) => {
  const copy = threadListCopy(organize.lang);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [taken, setTaken] = useState(false);
  const [newName, setNewName] = useState('');
  const renameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renamingId) renameRef.current?.focus();
  }, [renamingId]);

  const startRename = useCallback((section: ThreadSection<unknown>) => {
    setTaken(false);
    setDraft(section.label);
    setRenamingId(section.categoryId);
  }, []);

  const commitRename = useCallback(async (section: ThreadSection<unknown>) => {
    const clean = draft.trim();
    if (!clean || clean === section.label) {
      setRenamingId(null);
      return;
    }
    const ok = await organize.renameCategory(section.categoryId, clean);
    if (ok) setRenamingId(null);
    else {
      setTaken(true);
      renameRef.current?.focus();
    }
  }, [draft, organize]);

  const submitNew = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;
    const id = await organize.createCategory(name);
    if (id) setNewName('');
  }, [newName, organize]);

  const total = sections.length;
  return (
    <RailMenu
      ref={menuRef}
      role="menu"
      aria-label={copy.sectionsHeading}
      data-sections-manager=""
      data-prest0n-portal="true"
      $left={left}
      $top={top}
      onClick={(e) => e.stopPropagation()}
    >
      <RailMenuHeading>{copy.sectionsHeading}</RailMenuHeading>
      {sections.map((section, index) => {
        const isCustom = section.kind === 'category';
        const glyph = isCustom
          ? <CategoryFolderGlyph active size={12} />
          : <ThreadTypeGlyph kind={sectionGlyphKind(section.kind)} active size={12} />;
        const count = section.rows.length;
        return (
          <div key={section.key} style={rowStyle} data-manage-section={section.categoryId} data-manage-index={index}>
            {glyph}
            {renamingId === section.categoryId ? (
              <RailMenuInput
                ref={renameRef}
                type="text"
                data-manage-rename-input=""
                aria-label={copy.renameSection}
                aria-invalid={taken || undefined}
                title={taken ? copy.nameTaken : undefined}
                placeholder={copy.sectionNamePlaceholder}
                maxLength={THREAD_CATEGORY_NAME_MAX}
                value={draft}
                style={{ margin: 0, flex: '1 1 auto', minWidth: 0 }}
                onChange={(e) => { setDraft(e.target.value); setTaken(false); }}
                onBlur={() => { void commitRename(section); }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') { e.preventDefault(); void commitRename(section); }
                  if (e.key === 'Escape') { e.preventDefault(); setRenamingId(null); }
                }}
              />
            ) : (
              <>
                <span data-manage-label="" style={labelStyle} onDoubleClick={() => startRename(section)}>{section.label}</span>
                <span data-manage-count="" style={countStyle}>{count > 0 ? `· ${count}` : `· ${copy.emptySection}`}</span>
              </>
            )}
            <RailGhostIconButton
              type="button"
              role="menuitem"
              $always
              data-manage-up=""
              aria-label={`${copy.moveUp} · ${section.label}`}
              title={copy.moveUp}
              disabled={index <= 0}
              style={index <= 0 ? { opacity: 0.35, cursor: 'default' } : undefined}
              onClick={() => { void organize.moveCategory(section.categoryId, 'up'); }}
            >
              <ArrowUp />
            </RailGhostIconButton>
            <RailGhostIconButton
              type="button"
              role="menuitem"
              $always
              data-manage-down=""
              aria-label={`${copy.moveDown} · ${section.label}`}
              title={copy.moveDown}
              disabled={index >= total - 1}
              style={index >= total - 1 ? { opacity: 0.35, cursor: 'default' } : undefined}
              onClick={() => { void organize.moveCategory(section.categoryId, 'down'); }}
            >
              <ArrowDown />
            </RailGhostIconButton>
            <RailGhostIconButton
              type="button"
              role="menuitem"
              $always
              data-manage-rename=""
              aria-label={`${copy.renameSection} · ${section.label}`}
              title={copy.renameSection}
              onClick={() => startRename(section)}
            >
              <Pencil />
            </RailGhostIconButton>
            {isCustom ? (
              <RailGhostIconButton
                type="button"
                role="menuitem"
                $always
                data-manage-delete=""
                aria-label={`${copy.deleteSection} · ${section.label}`}
                title={copy.deleteSection}
                onClick={() => { void organize.removeCategory(section.categoryId); }}
              >
                <Cross />
              </RailGhostIconButton>
            ) : (
              // A built-in can be renamed and moved but never removed: keep the column aligned.
              <span aria-hidden="true" style={{ display: 'inline-block', width: 22, height: 22, flex: 'none' }} />
            )}
          </div>
        );
      })}
      <RailMenuInput
        type="text"
        data-manage-new-category=""
        aria-label={copy.newCategory}
        placeholder={copy.newCategoryPlaceholder}
        maxLength={THREAD_CATEGORY_NAME_MAX}
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') { e.preventDefault(); void submitNew(); }
        }}
      />
      <RailMenuItem type="button" role="menuitem" data-manage-done="" onClick={onClose}>{copy.done}</RailMenuItem>
    </RailMenu>
  );
};

export default ThreadSectionsManager;
